import { ChevronDown } from "lucide-react";
import * as React from "react";
import { Button, type ButtonProps } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type Option = { label: string; value: string };

interface SplitSelectButtonProps extends ButtonProps {
  options: Option[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
}

export const SplitSelectButton = React.forwardRef<
  HTMLButtonElement,
  SplitSelectButtonProps
>(
  (
    {
      options,
      value,
      defaultValue,
      onValueChange,
      children,
      className,
      ...props
    },
    ref,
  ) => {
    const isControlled = value !== undefined;

    const [internalValue, setInternalValue] = React.useState<
      string | undefined
    >(defaultValue ?? options?.[0]?.value);

    const currentValue = isControlled ? value : internalValue;

    const handleChange = (v: string): void => {
      if (!isControlled) setInternalValue(v);
      onValueChange?.(v);
    };

    return (
      <div className="flex">
        <Button
          ref={ref}
          className={`rounded-r-none ${className ?? ""}`}
          {...props}
        >
          {children}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              className="rounded-l-none px-2"
              disabled={props.disabled}
              size={props.size ?? "default"}
              type="button"
              variant={props.variant ?? "secondary"}
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end">
            <DropdownMenuRadioGroup
              value={currentValue}
              onValueChange={handleChange}
            >
              {options.map((opt) => (
                <DropdownMenuRadioItem key={opt.value} value={opt.value}>
                  {opt.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  },
);

SplitSelectButton.displayName = "SplitSelectButton";
